import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Clock, FileText, Briefcase, Rocket } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import { Button } from "@/components/ui/button";

const VISAS = [
  {
    key: "entrepreneur",
    icon: Briefcase,
    name: { vi: "Giấy phép cư trú diện Doanh nhân (Entrepreneur Residence Permit)", en: "Entrepreneur Residence Permit" },
    tagline: { vi: "Quyền cư trú dài hạn cho founder điều hành công ty tại Phần Lan", en: "Long-term residence permit for founders running a business in Finland" },
    duration: { vi: "Ban đầu 1-2 năm, gia hạn 4 năm/lần", en: "Initial 1-2 years, renewable 4 years at a time" },
    govFee: "€750",
    serviceFee: { vi: "Liên hệ báo giá", en: "Contact for quote" },
    processingTime: { vi: "2-6 tháng", en: "2-6 months" },
    included: [
      { vi: "Chuẩn bị hồ sơ theo yêu cầu ELY-keskus", en: "Prepare full ELY-keskus dossier" },
      { vi: "Viết business plan tiếng Anh/Phần Lan", en: "Business plan in English/Finnish" },
      { vi: "Chứng minh khả năng tài chính", en: "Financial viability proof" },
      { vi: "Hồ sơ bảo hiểm", en: "Insurance filings" },
      { vi: "Đại diện làm việc với Migri", en: "Represent you with Migri (immigration)" },
    ],
    bestFor: { vi: "Chủ doanh nghiệp có ý định gắn bó lâu dài với Phần Lan", en: "Business owners planning long-term operations in Finland" },
    recommended: true,
  },
  {
    key: "startup",
    icon: Rocket,
    name: { vi: "Giấy phép cư trú diện Startup (Startup Residence Permit)", en: "Startup Residence Permit" },
    tagline: { vi: "Dành cho founder startup có sản phẩm sáng tạo, mô hình scalable", en: "For innovative, scalable startups with strong growth potential" },
    duration: { vi: "2 năm, có thể gia hạn", en: "2 years, renewable" },
    govFee: "€650",
    serviceFee: { vi: "Liên hệ báo giá", en: "Contact for quote" },
    processingTime: { vi: "Tối đa 2 tháng", en: "Up to 2 months" },
    included: [
      { vi: "Xin Business Finland eligibility statement", en: "Business Finland eligibility statement" },
      { vi: "Chuẩn bị pitch deck và mô hình kinh doanh", en: "Pitch deck and business model prep" },
      { vi: "Kết nối với accelerator/incubator Phần Lan", en: "Introductions to Finnish accelerators/incubators" },
      { vi: "Hồ sơ Business Finland và Migri", en: "Business Finland and Migri dossier prep" },
      { vi: "Chứng minh khả năng tài chính", en: "Financial viability proof" },
      { vi: "Tư vấn chiến lược trong suốt quá trình", en: "Strategic advisory throughout the process" },
    ],
    bestFor: { vi: "Founder có công ty công nghệ, sản phẩm sáng tạo, có mô hình tăng trưởng nhanh", en: "Tech-driven founders with innovative, high-growth products" },
  },
];

export default function VisaSponsorship() {
  const { i18n } = useTranslation();
  const vi = i18n.language?.startsWith("vi");

  return (
    <div data-testid="visa-page" className="min-h-screen bg-background">
      <PublicHeader />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <Link to="/packages" data-testid="back-to-packages" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft className="w-4 h-4" /> {vi ? "Quay lại trang Gói dịch vụ" : "Back to Packages"}
        </Link>
        <div className="max-w-3xl">
          <div className="overline text-primary mb-4">{vi ? "CHƯƠNG TRÌNH ĐỊNH CƯ PHẦN LAN" : "FINLAND RESIDENCE PROGRAMS"}</div>
          <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter text-secondary leading-none">
            {vi
              ? "Chương trình định cư Phần Lan dành cho Doanh nhân & Nhà đầu tư."
              : "Finland residence programs for entrepreneurs & investors."}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            {vi
              ? "Nếu bạn cần quyền cư trú Phần Lan để trực tiếp điều hành công ty (thay cho mô hình chi nhánh mặc định), chúng tôi hỗ trợ toàn bộ lộ trình. Dưới đây là các diện giấy phép cư trú phổ biến cùng phí Nhà nước và phí dịch vụ của Safeway Finland."
              : "If you want to live in Finland and run your company directly (instead of the default branch model), we handle the whole residence permit journey for you. Below are the most common residence tracks — with government fees and Safeway Finland service fees."}
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {VISAS.map((v) => {
            const Icon = v.icon;
            return (
              <div
                key={v.key}
                data-testid={`visa-${v.key}`}
                className={`rounded-lg border p-8 card-lift flex flex-col ${
                  v.recommended
                    ? "bg-secondary text-secondary-foreground border-primary/50 relative"
                    : "bg-white border-border text-secondary"
                }`}
              >
                {v.recommended && (
                  <span className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[10px] font-heading font-black tracking-widest px-3 py-1 rounded-full">
                    {vi ? "PHỔ BIẾN NHẤT" : "MOST POPULAR"}
                  </span>
                )}
                <Icon className="w-7 h-7 text-primary mb-5" />
                <h3 className="font-heading text-xl font-bold tracking-tight">{v.name[vi ? "vi" : "en"]}</h3>
                <p className={`mt-2 text-sm ${v.recommended ? "text-secondary-foreground/70" : "text-muted-foreground"}`}>{v.tagline[vi ? "vi" : "en"]}</p>

                <div className="mt-6 pt-6 border-t border-border grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className={`uppercase tracking-widest ${v.recommended ? "text-secondary-foreground/60" : "text-muted-foreground"}`}>{vi ? "Phí nhà nước" : "Gov fee"}</div>
                    <div className={`mt-1 font-heading text-lg font-black ${v.recommended ? "text-secondary-foreground" : "text-secondary"}`}>{v.govFee}</div>
                  </div>
                  <div>
                    <div className={`uppercase tracking-widest ${v.recommended ? "text-secondary-foreground/60" : "text-muted-foreground"}`}>{vi ? "Phí dịch vụ" : "Service fee"}</div>
                    <div className="mt-1 font-heading font-black text-primary text-sm md:text-base leading-tight">{v.serviceFee[vi ? "vi" : "en"]}</div>
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-2 text-sm">
                  <Clock className={`w-4 h-4 ${v.recommended ? "text-primary" : "text-muted-foreground"}`} />
                  <span className={v.recommended ? "text-secondary-foreground/80" : "text-secondary"}>
                    {vi ? "Thời gian xử lý: " : "Processing: "}<strong>{v.processingTime[vi ? "vi" : "en"]}</strong>
                  </span>
                </div>
                <div className="mt-2 flex items-start gap-2 text-sm">
                  <FileText className={`w-4 h-4 mt-0.5 shrink-0 ${v.recommended ? "text-primary" : "text-muted-foreground"}`} />
                  <span className={v.recommended ? "text-secondary-foreground/80" : "text-secondary"}>
                    {vi ? "Thời hạn: " : "Duration: "}<strong>{v.duration[vi ? "vi" : "en"]}</strong>
                  </span>
                </div>

                <div className="mt-6 pt-6 border-t border-border">
                  <div className={`text-xs uppercase tracking-widest mb-3 ${v.recommended ? "text-primary" : "text-muted-foreground"}`}>
                    {vi ? "Phạm vi dịch vụ Safeway Finland" : "Safeway Finland scope of service"}
                  </div>
                  <ul className="space-y-2 text-sm">
                    {v.included.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${v.recommended ? "text-primary" : "text-primary"}`} />
                        <span className={v.recommended ? "text-secondary-foreground/90" : "text-secondary"}>{f[vi ? "vi" : "en"]}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`mt-6 pt-4 border-t border-border text-xs italic ${v.recommended ? "text-secondary-foreground/70" : "text-muted-foreground"}`}>
                  <strong>{vi ? "Phù hợp nhất: " : "Best for: "}</strong>{v.bestFor[vi ? "vi" : "en"]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legal note */}
        <div className="mt-12 rounded-md border border-primary/40 bg-primary/5 p-5 max-w-4xl text-sm text-secondary" data-testid="visa-legal-note">
          <strong>{vi ? "Lưu ý quan trọng: " : "Important note: "}</strong>
          {vi
            ? "Phí nhà nước là khoản thanh toán trực tiếp cho chính phủ Phần Lan và có thể thay đổi theo quy định hiện hành của Migri. Phí dịch vụ Safeway Finland được báo giá riêng theo từng hồ sơ — tuỳ vào mức độ phức tạp, số lượng thành viên gia đình đi kèm (vợ/chồng, con) và loại giấy phép — và đã bao gồm toàn bộ hồ sơ, biên dịch, cùng đại diện làm việc với cơ quan chức năng (Migri, ELY-keskus, Business Finland). Kết quả cấp phép do cơ quan chức năng quyết định — chúng tôi không đảm bảo 100% được duyệt nhưng có tỷ lệ thành công cao với hồ sơ chuẩn bị chuyên nghiệp."
            : "Government fees are paid directly to the Finnish government and are subject to change per Migri's current schedule. Safeway Finland service fees are quoted per case — depending on case complexity, number of accompanying family members (spouse/children) and permit track — and include the entire dossier, translations and representation with the Finnish authorities (Migri, ELY-keskus, Business Finland). Approval is at the authorities' discretion — we do not guarantee 100% success but our track record on well-prepared applications is strong."}
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/contact"><Button size="lg" className="h-12 px-8" data-testid="visa-contact">{vi ? "Trao đổi với chúng tôi" : "Talk to our team"}</Button></Link>
          <Link to="/packages"><Button size="lg" variant="outline" className="h-12 px-8">{vi ? "Xem lại 3 gói setup" : "Back to packages"}</Button></Link>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
